<template>
    <BaseModal :title="'Edit Contact'" @close="closeModal">
        <template #modalContent>
            <div class="modal-content-wrapper">
                <div>
                    <h3 class="modal-heading text-xl font-bold text-white p-2">Edit Contact Details</h3>
                </div>
                <div class="flex items-start justify-start h-full w-full">
                    <form @submit.prevent="updateContact" class="flex flex-col gap-4 mt-4 w-1/2">
                        <div>
                            <label for="name" class="text-lg text-gray-400">Name</label>
                            <input id="name" v-model="editedContact.name" type="text" placeholder="// John Doe"
                                class="input-field text-lg p-2 w-full" required @input="validateName" />
                            <p v-if="nameError" class="text-red-500 text-[10px] mt-1">{{ nameError }}</p>
                        </div>
                        <div>
                            <label for="email" class="text-lg text-gray-400">Email</label>
                            <input id="email" v-model="editedContact.email" type="email" placeholder="// example@gmail.com"
                                class="input-field text-lg p-2 w-full" required @input="validateEmail" />
                            <p v-if="emailError" class="text-red-500 text-[10px] mt-1">{{ emailError }}</p>
                        </div>
                        <div>
                            <label for="tags" class="text-lg text-gray-400">Tags</label>
                            <div class="dropdown-container relative">
                                <div class="input-field dropdown-toggle bg-[#1a1a1a] border border-[#374151] rounded-md p-2 flex items-center justify-between cursor-pointer"
                                    @click="toggleDropdown" @keydown.enter="toggleDropdown" tabindex="0"
                                    aria-label="Select tags" role="combobox" :aria-expanded="showDropdown">
                                    <span v-if="editedContact.tags.length === 0" class="text-gray-400 text-sm">
                                        // Select tags...
                                    </span>
                                    <div class="selected-tags flex flex-wrap gap-1">
                                        <span v-for="tag in editedContact.tags" :key="tag" class="tag-chip"
                                            :style="{ backgroundColor: getTagColor(tag) }">
                                            {{ tag }}
                                            <button class="remove-tag" @click.stop="removeTag(tag)"
                                                :aria-label="`Remove tag ${tag}`">
                                                ×
                                            </button>
                                        </span>
                                    </div>
                                    <span class="dropdown-arrow text-gray-400">▼</span>
                                </div>
                                <transition name="dropdown">
                                    <div v-if="showDropdown" class="dropdown-menu">
                                        <div v-for="tag in tags" :key="tag" class="dropdown-item"
                                            :class="{ 'selected': editedContact.tags.includes(tag) }"
                                            :style="{ backgroundColor: editedContact.tags.includes(tag) ? getTagColor(tag) : '' }"
                                            @click="toggleTag(tag)" @keydown.enter="toggleTag(tag)" tabindex="0"
                                            role="option" :aria-selected="editedContact.tags.includes(tag)">
                                            <span>{{ tag }}</span>
                                            <span v-if="editedContact.tags.includes(tag)" class="checkmark">✓</span>
                                        </div>
                                    </div>
                                </transition>
                            </div>
                        </div>
                        <div class="flex gap-2 w-full items-center justify-start">
                            <button type="submit" class="btn-create" :disabled="!!emailError || !!nameError">
                                Update Contact
                            </button>
                            <button type="button" @click="closeModal" class="btn-cancel">
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </template>
    </BaseModal>
</template>

<script>
import BaseModal from "@/components/common/BaseModal.vue";

export default {
    name: "EditContactModal",
    components: {
        BaseModal,
    },
    props: {
        contact: {
            type: Object,
            required: true,
        },
        tags: {
            type: Array,
            required: true,
        },
        closeModal: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            editedContact: {
                name: '',
                email: '',
                tags: [],
            },
            emailError: '',
            nameError: '',
            showDropdown: false,
            tagColors: {
                'Friends': '#1e40af',
                'Colleagues': '#4338ca',
                'Family': '#6b21a8',
                'Clients': '#9d174d',
                'Work': '#92400e',
                'Team': '#065f46'
            },
        };
    },
    created() {
        // Prefill form with contact data
        this.editedContact = {
            name: this.contact.name,
            email: this.contact.email,
            tags: [...this.contact.tags],
        };
    },
    methods: {
        validateEmail() {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!this.editedContact.email) {
                this.emailError = 'Email required';
            } else if (!emailRegex.test(this.editedContact.email)) {
                this.emailError = 'Invalid email';
            } else {
                this.emailError = '';
            }
        },
        validateName() {
            if (!this.editedContact.name) {
                this.nameError = 'Name required';
            } else {
                this.nameError = '';
            }
        },
        updateContact() {
            this.validateName();
            this.validateEmail();
            if (this.editedContact.email && !this.emailError && this.editedContact.name && !this.nameError) {
                this.$emit('update-contact', {
                    originalEmail: this.contact.email, // To identify the contact to update
                    updatedContact: { ...this.editedContact }
                });
                this.resetForm();
                this.closeModal();
                this.$vs.notify({
                    title: 'Success!',
                    text: 'Contact updated successfully',
                    color: 'success',
                });
            } else {
                this.$vs.notify({
                    title: 'Error!',
                    text: 'Please provide a valid name and email',
                    color: 'danger',
                });
            }
        },
        resetForm() {
            this.editedContact = { name: '', email: '', tags: [] };
            this.emailError = '';
            this.nameError = '';
            this.showDropdown = false;
        },
        toggleDropdown() {
            this.showDropdown = !this.showDropdown;
        },
        toggleTag(tag) {
            if (this.editedContact.tags.includes(tag)) {
                this.editedContact.tags = this.editedContact.tags.filter((t) => t !== tag);
            } else {
                this.editedContact.tags.push(tag);
            }
        },
        removeTag(tag) {
            this.editedContact.tags = this.editedContact.tags.filter((t) => t !== tag);
        },
        getTagColor(tag) {
            return this.tagColors[tag] || '#1f2937';
        },
        handleOutsideClick(event) {
            if (!this.$el.contains(event.target)) {
                this.showDropdown = false;
            }
        },
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideClick);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleOutsideClick);
    },
};
</script>

<style scoped>
.modal-content-wrapper {
    padding: 16px;
}

.input-field {
    background: #1a1a1a;
    border: 1px solid #374151;
    color: #fff;
    transition: border-color 0.3s;
}

.input-field:focus {
    outline: none;
    border-color: #22c55e;
}

.btn-create,
.btn-cancel {
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.3s ease;
    line-height: 1.5;
    text-align: center;
}

.btn-create {
    background: #22c55e;
    color: #000;
}

.btn-create:hover {
    background: #16a34a;
}

.btn-create:disabled {
    background: #4b5563;
    cursor: not-allowed;
}

.btn-cancel {
    background: #1a1a1a;
    border: 1px solid #ff5555;
    color: #ff5555;
}

.btn-cancel:hover {
    background: #ff5555;
    color: #000;
}

.dropdown-container {
    position: relative;
}

.dropdown-toggle {
    min-height: 40px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.selected-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    flex-grow: 1;
}

.tag-chip {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 4px;
}

.remove-tag {
    background: none;
    border: none;
    color: #fff;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    transition: color 0.3s;
}

.remove-tag:hover {
    color: #ff5555;
}

.dropdown-arrow {
    color: #9ca3af;
    font-size: 12px;
    flex-shrink: 0;
}

.dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #1a1a1a;
    border-radius: 6px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 100;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    margin-top: 4px;
}

.dropdown-item {
    padding: 8px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    color: #fff;
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s;
}

.dropdown-item:hover,
.dropdown-item:focus {
    background: #374151;
    color: #fff;
}

.dropdown-item.selected {
    color: #fff;
}

.checkmark {
    color: #fff;
    font-size: 14px;
}

.dropdown-enter-active,
.dropdown-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
</style>